import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { skipWhenOffline } from '../offline';
import { browserTimeZone } from '../utils/dateUtils';
import { useRBAC } from '../auth/useRBAC';
import { CLINIC_QUERY_KEY, useClinicRecord } from './useClinicTimezone';

/**
 * Set the practice's time zone ONCE, from the browser of the first OWNER/ADMIN
 * who signs in — then never touch it again.
 *
 * WHY THIS EXISTS. `Clinic.timezone` decides when the reminder tick fires, which
 * day a hospital charge lands on, what period a statement covers, and — since
 * PR #647 — what wall clock the web app prints for every instant on screen. It
 * became settable at all only in PR #646, and nothing backfills it, so a clinic
 * that has never opened Settings → Practice sits on the schema default. Asking
 * every practice to go and find that setting is a migration plan that does not
 * finish. The browser already knows the answer; `timezoneSetAt` is what lets the
 * app tell "nobody has said" from "somebody said Eastern" and act on the first
 * only (ADR-0034 / DATE-KINDS §3).
 *
 * WHAT IT DELIBERATELY IS NOT. It is not a correction: a clinic whose zone is
 * already stamped is never re-detected, so a practice that set Denver by hand
 * and then opens the app from an airport in Tokyo keeps Denver. And it is not a
 * silent write — the toast names the zone and where to change it, because a
 * value this load-bearing should not appear in the account without anyone being
 * told.
 *
 * GATES, all five: the role can actually write the field (`PUT /clinics/:id` is
 * `@Roles('OWNER','ADMIN')`, so a vet tech would just collect a 403), the clinic
 * payload actually CARRIES `timezoneSetAt` (an offline replica row predating the
 * column does not, and a missing key is not the same claim as a null one), that
 * stamp is null, the browser reports a zone the platform recognises as IANA (the
 * backend's `@IsIanaTimezone()` would reject anything else, and a failed
 * auto-detect must be invisible), and it has not already fired in this tab.
 */

/**
 * Clinic ids already attempted in this browser session. Module-level on purpose:
 * the layout that mounts this hook remounts on navigation, and a `useRef` alone
 * would let every remount re-fire the PATCH while the first is still in flight.
 *
 * ⚠ "Session" here is ONE TAB'S PAGE LIFETIME — module state, not storage. Two
 * tabs open at the same time each hold their own set, so both may PATCH; that is
 * harmless, because both send the SAME browser zone and `updateClinic` is
 * idempotent on it (the second write re-stamps `timezoneSetAt` to a slightly
 * later instant and changes nothing else). The same property is the retry
 * policy: a PATCH that FAILS is not retried in this page, and the next full page
 * load gets exactly one more attempt. Deliberately not `sessionStorage` — a
 * write that failed must stay retryable, and a per-tab set is the cheapest thing
 * that stops a remount loop without making the failure permanent.
 */
const attempted = new Set<string>();

/** Exported for the unit test only — a fresh `it()` needs a clean session. */
export function __resetAutoDetectForTest(): void {
  attempted.clear();
}

/**
 * `Intl` accepts the zone as a real IANA id. `resolvedOptions().timeZone` is
 * undefined on a few old engines and can be a non-IANA alias behind a hardened
 * `Intl`; both must end in "do nothing", never in a 400 on login.
 *
 * The `/` is a deliberate extra notch tighter than the backend's
 * `@IsIanaTimezone()`, which accepts bare `UTC` and the legacy `EST`/`PST`
 * aliases: a region-less id is what a MISCONFIGURED machine reports (a UTC
 * container, a laptop nobody set), not a statement about where a practice is.
 * Skipping those costs nothing — an unset clinic already renders in the
 * viewer's zone, which for a UTC browser is UTC either way — and it keeps the
 * one automatic writer of this column from stamping a machine default as an
 * answer.
 *
 * ⚠ `Etc/*` is rejected for the same reason and needs its own line, because the
 * `/` alone would let it through: `Etc/UTC`, `Etc/GMT`, `Etc/GMT+5` are real IANA
 * ids that `Intl` accepts happily, and they are exactly what a container or a
 * hardened browser reports when nobody has said where the machine is. `Etc/UTC`
 * is treated like bare `UTC` — never auto-set — so the column keeps meaning
 * "somebody said so". (The `Etc/GMT±N` ids are also sign-INVERTED by the POSIX
 * convention, so stamping one would be wrong twice over.) A practice genuinely
 * on UTC can still pick it by hand in Settings → Practice.
 */
export function isIanaTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !zone.trim() || !zone.includes('/')) return false;
  if (zone.startsWith('Etc/')) return false;
  try {
    // Throws RangeError for anything Intl does not recognise.
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function useAutoDetectClinicTimezone(): void {
  const { claims } = useRBAC();
  const { data: clinic } = useClinicRecord();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const inFlight = useRef(false);

  const clinicId = claims?.clinicId;
  const role = claims?.role;
  /**
   * The stamp has to be PRESENT before its absence can mean anything.
   *
   * `useClinicRecord` is served from the network OR from the offline replica,
   * and a replica row written before `timezoneSetAt` shipped simply has no such
   * key — indistinguishable, on a `!!clinic.timezoneSetAt` test, from a clinic
   * nobody has ever asked. That would let a practice that set Denver by hand be
   * silently re-detected from whatever browser opens the app next, which is the
   * one thing this hook promises never to do. `undefined` = "this payload does
   * not carry the field", so do nothing; `null` = "the field is here and nobody
   * has said", which is the only state that may fire. (`/sync`'s clinic payload
   * ships the key — `sync.service.ts` — so a fresh replica is a real answer.)
   */
  const stampPresent = clinic ? clinic.timezoneSetAt !== undefined : false;
  const alreadyKnown = !!clinic?.timezoneSetAt;

  useEffect(() => {
    if (inFlight.current) return;
    if (!clinicId || !clinic) return;
    if (role !== 'OWNER' && role !== 'ADMIN') return;
    // Both halves of the stamp gate: the field has to be THERE, and it has to be
    // null. A payload without the key is not an answer (see `stampPresent`).
    if (!stampPresent) return;
    if (alreadyKnown) return;
    if (attempted.has(clinicId)) return;
    // Offline the PATCH would only queue a write nobody asked for.
    if (!skipWhenOffline()) return;

    // The SAME reader the renders use, so the zone named in the toast is the zone
    // those screens were already showing. (Its own fallback is the schema
    // default, which `isIanaTimeZone` accepts — but a browser with no usable
    // `Intl` cannot render a date in this app at all, so that case is theoretical.)
    const zone = browserTimeZone();
    if (!isIanaTimeZone(zone)) return;

    attempted.add(clinicId);
    inFlight.current = true;
    api
      .put(`/clinics/${clinicId}`, { timezone: zone })
      .then(() => {
        queryClient.invalidateQueries(CLINIC_QUERY_KEY);
        enqueueSnackbar(
          `Practice time zone set to ${zone}. Change it in Settings → Practice.`,
          { variant: 'info', autoHideDuration: 8000 },
        );
      })
      // Non-blocking by construction: a failed detect leaves the zone UNKNOWN,
      // which is the state the app already renders correctly (the viewer's own
      // zone). Nothing is shown, and `attempted` was set BEFORE the call, so it
      // is not retried again in this page's lifetime — the next full page LOAD
      // (reload, or a fresh tab) tries once more, which is the retry policy: one
      // attempt per load, never a loop against a 500.
      .catch(() => undefined)
      .finally(() => {
        inFlight.current = false;
      });
  }, [clinicId, clinic, role, stampPresent, alreadyKnown, queryClient, enqueueSnackbar]);
}
